import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Phone, Mail, ChevronRight, Loader2, Wrench, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { name: "", phone: "", email: "", address: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [tab, setTab] = useState("clients"); // clients | services
  const navigate = useNavigate();

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const res = await api.get("/clients", { params: { search: q } });
      setClients(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (c, e) => { e.stopPropagation(); setForm({ ...c }); setEditing(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, phone: form.phone, email: form.email, address: form.address, notes: form.notes };
      if (editing) await api.put(`/clients/${editing}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setOpen(false);
      load(search);
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/clients/${toDelete.id}`);
      toast.success("Cliente eliminado");
      setToDelete(null);
      load(search);
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const toggleService = async (c, e) => {
    e.stopPropagation();
    const value = !c.is_service;
    try {
      await api.post(`/clients/${c.id}/service`, { value });
      setClients((prev) => prev.map((x) => x.id === c.id ? { ...x, is_service: value } : x));
      toast.success(value ? "Movido a Cliente Servicios" : "Quitado de Servicios");
    } catch (err) {
      toast.error("No se pudo mover");
    }
  };

  const visible = clients.filter((c) => tab === "services" ? c.is_service : !c.is_service);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            {tab === "services" ? "Cliente Servicios" : "Clientes"}
          </h1>
          <p className="text-muted-foreground mt-1">{visible.length} en total</p>
        </div>
        <Button onClick={openNew} data-testid="new-client-button">
          <Plus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Nuevo cliente</span>
        </Button>
      </div>

      <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="clients-tabs">
        <button onClick={() => setTab("clients")} data-testid="tab-clients"
          className={`px-4 py-2 text-sm font-medium transition-colors ${tab === "clients" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-accent"}`}>
          Clientes
        </button>
        <button onClick={() => setTab("services")} data-testid="tab-services"
          className={`px-4 py-2 text-sm font-medium transition-colors ${tab === "services" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-accent"}`}>
          Cliente Servicios
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por nombre, teléfono o email…" value={search}
          onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="client-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-muted-foreground">
          {tab === "services" ? "No hay clientes en Servicios. Mueve uno desde la pestaña Clientes." : "No hay clientes. Crea el primero."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="clients-list">
          {visible.map((c) => (
            <div key={c.id} onClick={() => navigate(`/clientes/${c.id}`)} data-testid={`client-item-${c.id}`}
              className="group flex items-center justify-between gap-3 rounded-md border border-border bg-card p-4 cursor-pointer hover:border-primary/50 transition-colors">
              <div className="min-w-0">
                <p className="font-medium truncate">{c.name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-muted-foreground mt-0.5">
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                  {c.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{c.email}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={(e) => toggleService(c, e)}
                  title={c.is_service ? "Quitar de Servicios" : "Mover a Servicios"}
                  data-testid={`toggle-service-${c.id}`}>
                  {c.is_service ? <Undo2 className="h-4 w-4 text-muted-foreground" /> : <Wrench className="h-4 w-4 text-primary" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={(e) => openEdit(c, e)} data-testid={`edit-client-${c.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setToDelete(c); }}
                  data-testid={`delete-client-${c.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="client-name-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="client-phone-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="client-email-input" />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="client-address-input" />
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="client-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-client-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará también todo su historial de trabajos y presupuestos asociados. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-client"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
